import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { notify } from '@/lib/notify';
import { useRole, useTier } from '@/stores/identity';
import {
  BANNER_COLOR_PALETTE,
  BANNER_SEVERITY_THEMES,
  autoTextColorForBg,
  createBanner,
  listOwnerBanners,
  updateBanner,
  uploadBannerImage,
} from '@/lib/banners';
import type { AppRole, Banner, BannerLayout, BannerSeverity, BannerTargetKind } from '@/lib/types';

const SEVERITIES: BannerSeverity[] = ['info', 'warning', 'success', 'promo'];
const ROLES: AppRole[] = ['owner', 'manager', 'staff'];
const LAYOUTS: { value: BannerLayout; label: string; hint: string }[] = [
  { value: 'compact', label: 'Compact', hint: 'Single-row strip. Good for short status updates.' },
  { value: 'media_left', label: 'Media left', hint: 'Image on the left, text middle, CTA right.' },
  { value: 'media_bg', label: 'Media background', hint: 'Image as a soft background. Good for promo.' },
];

function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function OwnerBannerEdit() {
  const role = useRole();
  const tier = useTier();
  const [confirmedOwner, setConfirmedOwner] = useState(false);
  useEffect(() => { if (role === 'owner') setConfirmedOwner(true); }, [role]);
  const isOwner = role === 'owner' || confirmedOwner;
  const canCompose = tier === 'pro' || tier === 'enterprise';
  const canRequireAck = tier === 'enterprise';
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [severity, setSeverity] = useState<BannerSeverity>('info');
  const [startsAt, setStartsAt] = useState(nowLocal());
  const [endsAt, setEndsAt] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [targetKind, setTargetKind] = useState<BannerTargetKind>('my_org_all');
  const [targetRoles, setTargetRoles] = useState<AppRole[]>([]);
  const [ackRequired, setAckRequired] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [layout, setLayout] = useState<BannerLayout>('compact');
  const [customBgHex, setCustomBgHex] = useState('');
  const [customTextHex, setCustomTextHex] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOwner || !isEdit) return;
    listOwnerBanners()
      .then((all) => {
        const found = all.find((b) => b.id === id);
        if (found) hydrate(found);
      })
      .catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, id]);

  function hydrate(b: Banner) {
    setTitle(b.title);
    setBodyMd(b.body_md);
    setSeverity(b.severity);
    setStartsAt(b.starts_at.slice(0, 16));
    setEndsAt(b.ends_at ? b.ends_at.slice(0, 16) : '');
    setCtaText(b.cta_text ?? '');
    setCtaUrl(b.cta_url ?? '');
    setTargetKind(b.target_kind === 'my_org_roles' ? 'my_org_roles' : 'my_org_all');
    setTargetRoles(b.target_roles);
    setAckRequired(b.acknowledgement_required);
    setImageUrl(b.image_url ?? '');
    setImageAlt(b.image_alt ?? '');
    setLayout(b.layout);
    setCustomBgHex(b.custom_bg_hex ?? '');
    setCustomTextHex(b.custom_text_hex ?? '');
  }

  async function onPickFile(file: File | null) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be 5 MB or smaller.'); return; }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadBannerImage(file);
      setImageUrl(url);
      if (!imageAlt && file.name) setImageAlt(file.name.replace(/\.[^.]+$/, '').slice(0, 80));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  if (role && role !== 'owner' && !confirmedOwner) {
    return <div className="container mx-auto py-8 px-4"><p className="text-sm text-muted-foreground">Owner only.</p></div>;
  }

  if (!canCompose) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto rounded-lg border border-border bg-card p-6 space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">Communications</h1>
          <p className="text-sm">Available on Pro and Enterprise plans.</p>
          <Link to="/billing" className="inline-block rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Upgrade</Link>
        </div>
      </div>
    );
  }

  function validate(): string | null {
    if (!title.trim()) return 'Title is required.';
    if (!bodyMd.trim()) return 'Body is required.';
    if (endsAt && endsAt <= startsAt) return 'End must be after start.';
    if ((ctaText && !ctaUrl) || (!ctaText && ctaUrl)) return 'Provide both CTA text and URL, or neither.';
    if (targetKind === 'my_org_roles' && targetRoles.length === 0) return 'Select at least one role.';
    if (ackRequired && !canRequireAck) return 'Acknowledgement-required is Enterprise-only.';
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        body_md: bodyMd.trim(),
        severity,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        cta_text: ctaText.trim() || null,
        cta_url: ctaUrl.trim() || null,
        target_kind: targetKind,
        target_roles: targetKind === 'my_org_roles' ? targetRoles : [],
        acknowledgement_required: ackRequired,
        image_url: imageUrl.trim() || null,
        image_alt: imageAlt.trim() || null,
        layout,
        custom_bg_hex: customBgHex.trim() || null,
        custom_text_hex: customTextHex.trim() || null,
      };
      if (isEdit && id) {
        await updateBanner(id, payload);
        notify.success('Banner updated');
      } else {
        await createBanner(payload);
        notify.success('Banner created (draft). Publish from the list page.');
      }
      navigate('/owner/banners', { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  const theme = BANNER_SEVERITY_THEMES[severity];
  const previewHasCustom = !!customBgHex && /^#[0-9a-fA-F]{6}$/.test(customBgHex);
  const previewFg = previewHasCustom
    ? (customTextHex && /^#[0-9a-fA-F]{6}$/.test(customTextHex) ? customTextHex : autoTextColorForBg(customBgHex))
    : undefined;
  const previewStyle: CSSProperties | undefined = previewHasCustom ? { background: customBgHex, color: previewFg } : undefined;
  const previewCtaStyle: CSSProperties | undefined = previewHasCustom ? { background: previewFg, color: customBgHex } : undefined;
  const previewWrapBg = previewHasCustom ? '' : theme.gradient;
  const previewWrapFg = previewHasCustom ? '' : theme.text;
  const previewCtaClass = previewHasCustom ? '' : theme.cta;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-1">
          <button onClick={() => navigate('/owner/banners')} className="text-xs text-muted-foreground hover:underline">← Back to communications</button>
          <h1 className="text-2xl font-semibold tracking-tight">{isEdit ? 'Edit banner' : 'New banner'}</h1>
        </div>

        <div className="rounded-lg border border-border overflow-hidden bg-muted/10 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground px-1">Preview</p>
          <div
            className={`${previewWrapBg} ${previewWrapFg} rounded-lg shadow-sm ring-1 ${theme.ring} px-4 py-2.5`}
            style={previewStyle}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">{title || 'Banner title…'}</p>
                {bodyMd && <p className="text-xs whitespace-pre-wrap opacity-95 mt-0.5">{bodyMd}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {ctaText && ctaUrl && (
                  <span className={`${previewCtaClass} rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm`} style={previewCtaStyle}>
                    {ctaText}
                  </span>
                )}
                <span className="text-xs font-medium">{ackRequired ? 'I acknowledge' : 'Dismiss'}</span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
        )}

        <form onSubmit={onSubmit} className="space-y-5 rounded-lg border border-border bg-card p-6">
          <Field label="Title">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={inputClass}
              placeholder="e.g. New hand-off SOP — effective Monday" />
          </Field>
          <Field label="Body" hint="Plain text. Line breaks preserved.">
            <textarea rows={3} value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} className={inputClass}
              placeholder="What do you need your team to know?" />
          </Field>
          <Field label="Severity">
            <div className="flex flex-wrap gap-2">
              {SEVERITIES.map((s) => {
                const t = BANNER_SEVERITY_THEMES[s];
                return (
                  <button type="button" key={s} onClick={() => setSeverity(s)}
                    className={`rounded-full px-3 py-1 text-xs ${severity === s ? `${t.bg} ${t.text}` : 'border border-border bg-background hover:bg-muted/40'}`}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Background color" hint="Optional override of the severity color.">
            <div className="flex flex-wrap items-center gap-2">
              {BANNER_COLOR_PALETTE.map((c) => (
                <button key={c.hex} type="button" title={`${c.name} (${c.hex})`} onClick={() => setCustomBgHex(c.hex)}
                  style={{ background: c.hex }}
                  className={`h-7 w-7 rounded-md ring-1 ring-border ${customBgHex.toLowerCase() === c.hex.toLowerCase() ? 'ring-2 ring-offset-2 ring-foreground' : ''}`}
                  aria-label={c.name} />
              ))}
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(customBgHex) ? customBgHex : '#1e3a8a'}
                onChange={(e) => setCustomBgHex(e.target.value)}
                className="h-7 w-9 rounded border border-border bg-transparent cursor-pointer" />
              <input type="text" value={customBgHex} onChange={(e) => setCustomBgHex(e.target.value)} maxLength={7} placeholder="#1e3a8a"
                className={`${inputClass} w-28 font-mono text-xs`} />
              {customBgHex && (
                <button type="button" onClick={() => { setCustomBgHex(''); setCustomTextHex(''); }} className="text-xs underline text-muted-foreground">Reset</button>
              )}
            </div>
          </Field>

          <Field label="Layout">
            <div className="flex flex-wrap gap-2">
              {LAYOUTS.map((l) => (
                <button type="button" key={l.value} onClick={() => setLayout(l.value)}
                  className={`rounded-full border px-3 py-1 text-xs ${layout === l.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover:bg-muted/40'}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{LAYOUTS.find((l) => l.value === layout)?.hint}</p>
          </Field>

          <Field label="Image (optional)" hint="Up to 5 MB. JPEG / PNG / WebP / GIF.">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)} className="hidden" />
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted/40 disabled:opacity-50">
                {uploading ? 'Uploading…' : 'Upload image'}
              </button>
              <span className="text-xs text-muted-foreground">or</span>
              <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
                className={`${inputClass} flex-1 min-w-[14rem]`} placeholder="https://…" />
              {imageUrl && <button type="button" onClick={() => { setImageUrl(''); setImageAlt(''); }} className="rounded-md border border-border px-3 py-2 text-xs">Clear</button>}
            </div>
          </Field>
          {imageUrl && (
            <Field label="Image alt text" hint="Used by screen readers.">
              <input type="text" value={imageAlt} onChange={(e) => setImageAlt(e.target.value)} className={inputClass} />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts">
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Ends (optional)">
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inputClass} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA text (optional)">
              <input type="text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} className={inputClass} placeholder="Read SOP" />
            </Field>
            <Field label="CTA URL (optional)">
              <input type="url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className={inputClass} placeholder="https://…" />
            </Field>
          </div>

          <Field label="Audience">
            <select value={targetKind} onChange={(e) => setTargetKind(e.target.value as BannerTargetKind)} className={inputClass}>
              <option value="my_org_all">All my staff</option>
              <option value="my_org_roles">Specific roles only</option>
            </select>
            {targetKind === 'my_org_roles' && (
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={targetRoles.includes(r)}
                      onChange={(e) => setTargetRoles((cur) => e.target.checked ? [...cur, r] : cur.filter((x) => x !== r))} />
                    {r}
                  </label>
                ))}
              </div>
            )}
          </Field>

          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={ackRequired} disabled={!canRequireAck}
                onChange={(e) => setAckRequired(e.target.checked)} className="mt-1 h-4 w-4 rounded border border-border disabled:opacity-50" />
              <span className={canRequireAck ? '' : 'text-muted-foreground'}>
                <strong>Require acknowledgement.</strong> Dismiss button reads "I acknowledge".
                You'll get a per-staff read-receipt report.
              </span>
            </label>
            {!canRequireAck && (
              <p className="text-xs text-muted-foreground pl-6">
                Enterprise tier only. <Link to="/billing" className="underline">Upgrade</Link>.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={submitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Save as draft'}
            </button>
            <button type="button" onClick={() => navigate('/owner/banners')} className="rounded-md border border-border bg-background px-4 py-2 text-sm">
              Cancel
            </button>
          </div>
          <p className="text-xs text-muted-foreground">New banners start as <strong>draft</strong>. Publish from the list page.</p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
